import InputLabel from '@mui/material/InputLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextInput from '../../components/TextInput';
import React, { useState } from 'react';
import { useGroupsContext } from '../../stores/groups-context';
import { Identifier } from '../../types/identifier';
import { JobGroup } from '../../types/group';
import useConfigStyles from '../../components/useConfigStyles';

type JobGroupSelectProps = {
  groups: JobGroup[];
  addGroup: (group: JobGroup) => void;
};

const DefaultSelectGroup: Identifier = {
  Id: '0',
  Name: '',
};

const severity = ['1-ERROR', '2-WARNING', '3-INFORMATION'];

function JobGroupSelect({ groups, addGroup }: JobGroupSelectProps): JSX.Element {
  const [selectedGroup, setSelectedGroup] = useState<Identifier>(DefaultSelectGroup);
  const [jobGroupSeverity, setJobGroupSeverity] = useState(severity[0]);
  const [jobGroupEnabled, setJobGroupEnabled] = useState(true);
  const [buttonLabel, setButtonLabel] = useState('Add');
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const groupsContext = useGroupsContext();
  const classes = useConfigStyles();

  const updateButton = (groupName: string) => {
    if (groups.findIndex((v) => v.Name === groupName) >= 0) {
      setButtonLabel('Update');
    } else {
      setButtonLabel('Add');
    }
    setButtonEnabled(groupName !== '0');
  };

  const handleGroupSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedGroup({ Id: value, Name: value });
    updateButton(value);
  };

  const handleSeveritySelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setJobGroupSeverity(value);
  };

  const handleOnClick = () => {
    const groupToAddOrUpdate = groupsContext.data.find((g) => g.Name === selectedGroup.Name);
    if (groupToAddOrUpdate) {
      addGroup({
        ...groupToAddOrUpdate,
        Severity: jobGroupSeverity,
        Disabled: !jobGroupEnabled,
      });
      setSelectedGroup(DefaultSelectGroup);
      setJobGroupSeverity(severity[0]);
      setJobGroupEnabled(true);
      setButtonEnabled(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJobGroupEnabled(event.target.checked);
  };

  return (
    <Grid container spacing={2} alignItems='center' justifyContent='flex-end' className={classes.container}>
      <Grid item md={10}>
        <TextInput name='Groups' label='Groups' value={selectedGroup.Id} onChange={handleGroupSelect} select>
          <MenuItem value='0'>Select...</MenuItem>
          {groupsContext.data.map((group) => {
            return (
              <MenuItem key={group.Name} value={group.Name}>
                {group.Name}
              </MenuItem>
            );
          })}
        </TextInput>
      </Grid>
      <Grid item md={5}>
        <TextInput name='Severity' label='Severity' value={jobGroupSeverity} onChange={handleSeveritySelect} select>
          {severity.map((s) => {
            return (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            );
          })}
        </TextInput>
      </Grid>
      <Grid item md={3}>
        <InputLabel>Enabled</InputLabel>
        <Switch checked={jobGroupEnabled} onChange={handleChange} color='primary' name='Enabled' />
      </Grid>
      <Grid item md={2}>
        <Button variant='contained' color='primary' type='button' onClick={handleOnClick} disabled={!buttonEnabled}>
          {buttonLabel}
        </Button>
      </Grid>
    </Grid>
  );
}

export default JobGroupSelect;
